import { addDays, eachDayOfInterval, endOfDay, isWithinInterval, startOfDay } from 'date-fns';

import { type AkaVeranstaltungId, Location, type Raum, type Zeitraum } from '../../../../../dtos.js';
import { content } from '../../../../../utils';
import { type CalendarEvent, CalendarRaumEvent } from '../CalendarEvent';
import { calendarResourceTypes } from '../calendarResourceTypes.js';
import { SuggestedEvents } from './AbstractCalendarResource.js';
import { eventCreationStrategies, type EventCreationStrategy } from './AbstractCalendarResource.types.js';
import { AbstractRaumResource } from './AbstractRaumResource.js';

/**
 * Seminarraum ist der Haupt-Raum der Veranstaltung: Standardraum (in Hotel) | Raum in PC-Center | Raum in Veranstaltungslocation
 */
export class CalendarSeminarraumResource extends AbstractRaumResource {
  public readonly type = calendarResourceTypes.SEMINARRAUM;

  public readonly id: `r-${string}`;

  public readonly name: string;

  public readonly eventCreationStrategy: EventCreationStrategy =
    eventCreationStrategies.SINGLE_EVENT_SPANNING_ALL_ABLAUFTAGE;

  public constructor(raum: Raum, location: Location) {
    super(raum, location);
    this.id = `r-${raum.raumSapId}`;
    this.name = `${raum.kuerzel} / ${raum.name}`;
    this.subtext = `${raum.groesse}${content.unitsWs.qm} / ${raum.kapazitaetUForm}-${raum.kapazitaetParlamentarisch}-${raum.kapazitaetOhneTische}`;
  }

  public createPendingEventsFromBlockungen(akaVeranstaltungId: AkaVeranstaltungId): CalendarEvent[] {
    return this.createFixedEventsFromBlockungen(akaVeranstaltungId)
      .filter((event) => event.isOldSelection)
      .map((event) => {
        event.isOldSelection = false;
        event.isCurrentSelection = true;
        event.editable = true;
        event.resize = false;

        return event;
      });
  }

  public createPendingEventsFromSuggestions(
    suggestedEvents: SuggestedEvents,
    akaVeranstaltungId: AkaVeranstaltungId | null,
  ) {
    if (
      !suggestedEvents.seminarraumSapId ||
      suggestedEvents.seminarraumSapId !== this.raum.raumSapId ||
      suggestedEvents.tage.length === 0
    ) {
      return [];
    }

    const newZeitraum = {
      start: suggestedEvents.tage[0].zeitraum.start,
      end: suggestedEvents.tage[suggestedEvents.tage.length - 1].zeitraum.end,
    };

    const newEvent = new CalendarRaumEvent(newZeitraum.start, newZeitraum.end, this, {
      veranstaltung: null,
      quelleTerminId: akaVeranstaltungId ?? '',
      zeitraeume: suggestedEvents.tage.map((tag) => tag.zeitraum),
    });
    newEvent.resize = false;
    newEvent.isOldSelection = false;
    newEvent.isCurrentSelection = true;
    newEvent.editable = true;

    return [newEvent];
  }

  protected computeNewCalendarEventBounds(date: Date, ablaufTageCount: number): Zeitraum {
    const start = startOfDay(date);
    const end = endOfDay(addDays(date, ablaufTageCount - 1));

    return { start, end };
  }

  public isAvailable(interval: Zeitraum) {
    const days = eachDayOfInterval(interval);
    return days.every((date) =>
      // remove a minute off the end because of non-exclusivity of mobiscroll events, otherwise the last day would be in interval although it should not
      this.raum.gekaufterZeitraum.some((tag) =>
        isWithinInterval(date, { start: startOfDay(tag.start), end: endOfDay(tag.end.valueOf() - 60_000) }),
      ),
    );
  }

  public isBookableAt(date: Date): boolean {
    return (
      this.raum.gekaufterZeitraum.some((zeitraum) => isWithinInterval(date, zeitraum)) &&
      !this.raum.raumBlockungen.some((blockung) =>
        blockung.zeitraeume.some((zeitraum) => isWithinInterval(date, zeitraum)),
      )
    );
  }
}
